import Phaser from 'phaser';
import type { CoreEvent } from '../../core/types';
import { EventCues, type Cue } from './cues';
import { Crossfade } from './fader';
import { effectiveVolume, type AudioSettings } from './settings';
import { pitchDetune, VoiceLimiter } from './voices';
import { DEBUG } from '../debug';

/** Section `audio` du manifest, écrite par tools/build-audio.py. */
export interface SfxDef { file: string; volume: number; duration: number; vary: boolean; }
export interface MusicDef { file: string; volume: number; duration: number; loop: boolean; }
export interface AudioManifest { sfx: Record<string, SfxDef>; music: Record<string, MusicDef>; }

export const sfxKey = (key: string): string => `sfx:${key}`;
export const musicKey = (key: string): string => `music:${key}`;

/** Avec ?debug=1 : état audio exposé aux tests de bout en bout. */
export interface AudioDebug { music: string | null; musicPlaying: boolean; locked: boolean; played: Record<string, number>; }
declare global { interface Window { __rogfusAudio?: AudioDebug; } }

type Sound = Phaser.Sound.NoAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;

const LOADER_SCENE = 'AudioLoader';
/** Scène vide, jamais arrêtée : son loader charge les fichiers audio sans être remis à zéro quand le Hub ou la salle s'arrête. */
class AudioLoaderScene extends Phaser.Scene {
  constructor() { super(LOADER_SCENE); }
}

/**
 * Lecture des effets et des musiques, découplée du cœur : reçoit les CoreEvent, choisit les sons (cues.ts),
 * limite les voix (voices.ts), varie la hauteur des sons répétés et enchaîne les musiques en fondu (fader.ts).
 * Les fichiers sont chargés après le démarrage (`load`) : un échec audio ne bloque jamais le jeu.
 */
export class AudioManager {
  private manifest: AudioManifest = { sfx: {}, music: {} };
  private settings: AudioSettings;
  private readonly limiter = new VoiceLimiter();
  private readonly cues = new EventCues();
  private readonly fade = new Crossfade(800);
  private readonly tracks = new Map<string, Sound>();
  private pendingMusic: string | null = null;
  private loading = false;
  private readonly played: Record<string, number> = {};
  private readonly onStep: (time: number, delta: number) => void;

  constructor(private readonly game: Phaser.Game, settings: AudioSettings, private readonly random: () => number = Math.random) {
    this.settings = { ...settings };
    this.onStep = (_t, delta) => this.step(delta);
    game.events.on(Phaser.Core.Events.STEP, this.onStep);
    game.scene.add(LOADER_SCENE, AudioLoaderScene, true);
    this.applyMute();
  }

  /** Charge tous les fichiers du manifest (une seule fois) sur la scène de chargement dédiée, après le démarrage du jeu. */
  load(manifest: AudioManifest | undefined): void {
    if (!manifest || this.loading) return;
    const scene = this.game.scene.getScene(LOADER_SCENE) as Phaser.Scene | null;
    if (!scene?.sys.isActive()) { this.game.events.once(Phaser.Core.Events.STEP, () => this.load(manifest)); return; }
    const loader = scene.load;
    this.manifest = manifest;
    this.loading = true;
    for (const [key, def] of Object.entries(manifest.sfx)) if (!this.game.cache.audio.exists(sfxKey(key))) loader.audio(sfxKey(key), `/assets/${def.file}`);
    for (const [key, def] of Object.entries(manifest.music)) if (!this.game.cache.audio.exists(musicKey(key))) loader.audio(musicKey(key), `/assets/${def.file}`);
    loader.on(Phaser.Loader.Events.FILE_COMPLETE, (key: string) => { if (this.pendingMusic && key === musicKey(this.pendingMusic)) this.music(this.pendingMusic); });
    loader.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => { console.warn('Audio introuvable :', file.key); });
    loader.start();
  }

  get current(): AudioSettings { return { ...this.settings }; }

  /** Applique de nouveaux réglages (volumes, muet) aux sons en cours et à venir. */
  apply(settings: AudioSettings): void {
    this.settings = { ...settings };
    this.applyMute();
    for (const [key, sound] of this.tracks) {
      const def = this.manifest.music[key];
      if (def && sound.isPlaying) sound.setVolume(effectiveVolume(def.volume, this.settings.music, false) * this.level(key));
    }
  }

  /** Joue un effet du catalogue ; `false` si inconnu, pas encore chargé, muet ou refusé par la limite de voix. */
  play(key: string): boolean {
    const def = this.manifest.sfx[key];
    if (!def || this.settings.muted || this.settings.sfx <= 0) return false;
    if (!this.game.cache.audio.exists(sfxKey(key))) return false;
    const now = this.game.getTime();
    if (!this.limiter.request(key, now, def.duration * 1000)) return false;
    const volume = effectiveVolume(def.volume, this.settings.sfx, false);
    const detune = def.vary ? pitchDetune(this.random) : 0;
    const ok = this.game.sound.play(sfxKey(key), { volume, detune });
    if (ok) this.played[key] = (this.played[key] ?? 0) + 1;
    return ok;
  }

  /** Musique de fond (`null` = silence) en fondu enchaîné ; attend le chargement de la piste et le déverrouillage du navigateur. */
  music(key: string | null): void {
    if (key !== null && !this.manifest.music[key]) return;
    if (key !== null && !this.game.cache.audio.exists(musicKey(key))) { this.pendingMusic = key; return; }
    if (key !== null && this.game.sound.locked) {
      this.pendingMusic = key;
      this.game.sound.once(Phaser.Sound.Events.UNLOCKED, () => { if (this.pendingMusic) this.music(this.pendingMusic); });
      return;
    }
    this.pendingMusic = null;
    const out = this.fade.start(key);
    for (const k of out.stop) this.stopTrack(k);
    if (key !== null) {
      const sound = this.track(key);
      if (sound && !sound.isPlaying) { sound.setVolume(0); sound.play(); }
    }
    this.applyLevels(out.set);
  }

  get currentMusic(): string | null { return this.fade.currentKey; }

  /** Point d'entrée depuis la scène : les événements d'un pas du cœur. */
  onEvents(events: CoreEvent[], info: { playerId: number | null; roomsTotal: number }): void {
    for (const cue of this.cues.cuesFor(events, info)) this.handle(cue);
  }

  /** Nouvelle run : oublie la salle suivie pour le boss. */
  resetRun(): void { this.cues.reset(); this.limiter.reset(); }

  dispose(): void {
    this.game.events.off(Phaser.Core.Events.STEP, this.onStep);
    for (const k of [...this.tracks.keys()]) this.stopTrack(k);
  }

  private handle(cue: Cue): void {
    if (cue.kind === 'sfx') this.play(cue.key);
    else this.music(cue.key);
  }

  private step(deltaMs: number): void {
    const out = this.fade.update(deltaMs);
    for (const k of out.stop) this.stopTrack(k);
    this.applyLevels(out.set);
    if (DEBUG) {
      const cur = this.fade.currentKey;
      window.__rogfusAudio = { music: cur, musicPlaying: cur !== null && (this.tracks.get(cur)?.isPlaying ?? false), locked: this.game.sound.locked, played: { ...this.played } };
    }
  }

  private level(key: string): number {
    // niveau courant du fondu pour une piste : 1 quand stable (le fondu ne publie que les variations)
    return this.levels.get(key) ?? 1;
  }
  private readonly levels = new Map<string, number>();

  private applyLevels(set: { key: string; level: number }[]): void {
    for (const { key, level } of set) {
      this.levels.set(key, level);
      const def = this.manifest.music[key];
      const sound = this.tracks.get(key);
      if (def && sound) sound.setVolume(effectiveVolume(def.volume, this.settings.music, false) * level);
    }
  }

  private track(key: string): Sound | null {
    let s = this.tracks.get(key);
    if (s) return s;
    const def = this.manifest.music[key];
    if (!def) return null;
    s = this.game.sound.add(musicKey(key), { loop: def.loop, volume: 0 });
    this.tracks.set(key, s);
    return s;
  }

  private stopTrack(key: string): void {
    const s = this.tracks.get(key);
    if (s) { s.stop(); s.destroy(); }
    this.tracks.delete(key);
    this.levels.delete(key);
  }

  private applyMute(): void { this.game.sound.setMute(this.settings.muted); }
}
